import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Banknote, Layers, Plus, Save } from "lucide-react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { ErrorPanel, Loading } from "../components/Loading";
import { EmptyState, PageHeader } from "../components/ui";
import { BilingualText } from "../components/ui/BilingualText";
import { api } from "../lib/api";
import type { CompBandDTO, GradeDetail } from "../lib/api";
import { useLegalEntityGate } from "../lib/legalEntity";

// M3X.COMP F.3 — per-LE comp band catalog editor.
// Left pane: list of grades (with band-count badges).
// Right pane: bands for the selected grade with inline edit.
// Bands are DET catalog rows; money math is never AI per Commandment 3.
// No equity columns anywhere per Amer's scope lock at Q2.

interface BandFormState {
  grade_step: number;
  min_salary: string;
  mid_salary: string;
  max_salary: string;
  bonus_target_pct_of_base: string;
  bonus_cap_pct_of_base: string;
  bonus_cycle_frequency: string;
  market_p25_salary: string;
  market_p50_salary: string;
  market_p75_salary: string;
  market_p90_salary: string;
  market_data_source: string;
  market_data_as_of: string;
}

const EMPTY_FORM: BandFormState = {
  grade_step: 1,
  min_salary: "",
  mid_salary: "",
  max_salary: "",
  bonus_target_pct_of_base: "0",
  bonus_cap_pct_of_base: "0",
  bonus_cycle_frequency: "",
  market_p25_salary: "",
  market_p50_salary: "",
  market_p75_salary: "",
  market_p90_salary: "",
  market_data_source: "",
  market_data_as_of: "",
};

function bandToForm(b: CompBandDTO): BandFormState {
  return {
    grade_step: b.grade_step,
    min_salary: b.min_salary,
    mid_salary: b.mid_salary,
    max_salary: b.max_salary,
    bonus_target_pct_of_base: b.bonus_target_pct_of_base,
    bonus_cap_pct_of_base: b.bonus_cap_pct_of_base,
    bonus_cycle_frequency: b.bonus_cycle_frequency ?? "",
    market_p25_salary: b.market_p25_salary ?? "",
    market_p50_salary: b.market_p50_salary ?? "",
    market_p75_salary: b.market_p75_salary ?? "",
    market_p90_salary: b.market_p90_salary ?? "",
    market_data_source: b.market_data_source ?? "",
    market_data_as_of: b.market_data_as_of ?? "",
  };
}

function formToBody(
  legalEntityId: string,
  gradeId: string,
  form: BandFormState,
): Record<string, unknown> {
  const body: Record<string, unknown> = {
    legal_entity_id: legalEntityId,
    grade_id: gradeId,
    grade_step: form.grade_step,
    min_salary: form.min_salary,
    mid_salary: form.mid_salary,
    max_salary: form.max_salary,
    bonus_target_pct_of_base: form.bonus_target_pct_of_base || "0",
    bonus_cap_pct_of_base: form.bonus_cap_pct_of_base || "0",
  };
  if (form.bonus_cycle_frequency) {
    body.bonus_cycle_frequency = form.bonus_cycle_frequency;
  }
  if (form.market_p25_salary) body.market_p25_salary = form.market_p25_salary;
  if (form.market_p50_salary) body.market_p50_salary = form.market_p50_salary;
  if (form.market_p75_salary) body.market_p75_salary = form.market_p75_salary;
  if (form.market_p90_salary) body.market_p90_salary = form.market_p90_salary;
  if (form.market_data_source) {
    body.market_data_source = form.market_data_source;
  }
  if (form.market_data_as_of) body.market_data_as_of = form.market_data_as_of;
  return body;
}

interface BandFormProps {
  band: CompBandDTO | null; // null = create
  legalEntityId: string;
  gradeId: string;
  onSaved: () => void;
}

function BandForm({ band, legalEntityId, gradeId, onSaved }: BandFormProps) {
  const { t } = useTranslation();
  const [form, setForm] = useState<BandFormState>(
    band ? bandToForm(band) : EMPTY_FORM,
  );
  const [error, setError] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: async () => {
      if (band) {
        return api.updateCompBand(band.id, formToBody(legalEntityId, gradeId, form));
      }
      return api.createCompBand(formToBody(legalEntityId, gradeId, form));
    },
    onSuccess: () => {
      setError(null);
      onSaved();
    },
    onError: (e: Error) => setError(e.message),
  });

  const setField = <K extends keyof BandFormState>(
    key: K,
    value: BandFormState[K],
  ) => setForm((prev) => ({ ...prev, [key]: value }));

  return (
    <div className="space-y-4 rounded border border-slate-200 bg-white p-4">
      <div className="text-sm font-medium text-slate-700">
        {band
          ? t("comp_bands.edit_step", { step: form.grade_step })
          : t("comp_bands.new_band")}
      </div>

      <div className="grid grid-cols-2 gap-3">
        <label className="flex flex-col text-xs">
          <span className="text-slate-600">{t("comp_bands.grade_step")}</span>
          <input
            type="number"
            min={1}
            value={form.grade_step}
            disabled={!!band}
            onChange={(e) => setField("grade_step", Number(e.target.value))}
            className="mt-1 rounded border border-slate-300 px-2 py-1 text-sm disabled:bg-slate-50"
          />
        </label>
      </div>

      <fieldset className="space-y-2">
        <legend className="text-xs font-medium text-slate-700">
          {t("comp_bands.salary_range")}
        </legend>
        <div className="grid grid-cols-3 gap-3">
          <label className="flex flex-col text-xs">
            <span className="text-slate-600">{t("comp_bands.min")}</span>
            <input
              type="number"
              min={0}
              step="0.01"
              value={form.min_salary}
              onChange={(e) => setField("min_salary", e.target.value)}
              className="mt-1 rounded border border-slate-300 px-2 py-1 text-sm"
            />
          </label>
          <label className="flex flex-col text-xs">
            <span className="text-slate-600">{t("comp_bands.mid")}</span>
            <input
              type="number"
              min={0}
              step="0.01"
              value={form.mid_salary}
              onChange={(e) => setField("mid_salary", e.target.value)}
              className="mt-1 rounded border border-slate-300 px-2 py-1 text-sm"
            />
          </label>
          <label className="flex flex-col text-xs">
            <span className="text-slate-600">{t("comp_bands.max")}</span>
            <input
              type="number"
              min={0}
              step="0.01"
              value={form.max_salary}
              onChange={(e) => setField("max_salary", e.target.value)}
              className="mt-1 rounded border border-slate-300 px-2 py-1 text-sm"
            />
          </label>
        </div>
      </fieldset>

      <fieldset className="space-y-2">
        <legend className="text-xs font-medium text-slate-700">
          {t("comp_bands.bonus_eligibility")}
        </legend>
        <div className="grid grid-cols-3 gap-3">
          <label className="flex flex-col text-xs">
            <span className="text-slate-600">
              {t("comp_bands.bonus_target_pct")}
            </span>
            <input
              type="number"
              min={0}
              max={200}
              step="0.01"
              value={form.bonus_target_pct_of_base}
              onChange={(e) =>
                setField("bonus_target_pct_of_base", e.target.value)
              }
              className="mt-1 rounded border border-slate-300 px-2 py-1 text-sm"
            />
          </label>
          <label className="flex flex-col text-xs">
            <span className="text-slate-600">
              {t("comp_bands.bonus_cap_pct")}
            </span>
            <input
              type="number"
              min={0}
              max={500}
              step="0.01"
              value={form.bonus_cap_pct_of_base}
              onChange={(e) =>
                setField("bonus_cap_pct_of_base", e.target.value)
              }
              className="mt-1 rounded border border-slate-300 px-2 py-1 text-sm"
            />
          </label>
          <label className="flex flex-col text-xs">
            <span className="text-slate-600">
              {t("comp_bands.bonus_cycle")}
            </span>
            <select
              value={form.bonus_cycle_frequency}
              onChange={(e) =>
                setField("bonus_cycle_frequency", e.target.value)
              }
              className="mt-1 rounded border border-slate-300 px-2 py-1 text-sm"
            >
              <option value="">{t("comp_bands.cycle_none")}</option>
              <option value="annual">{t("comp_bands.cycle_annual")}</option>
              <option value="semi_annual">
                {t("comp_bands.cycle_semi_annual")}
              </option>
              <option value="quarterly">
                {t("comp_bands.cycle_quarterly")}
              </option>
            </select>
          </label>
        </div>
      </fieldset>

      <fieldset className="space-y-2">
        <legend className="text-xs font-medium text-slate-700">
          {t("comp_bands.market_data")}
        </legend>
        <div className="grid grid-cols-4 gap-3">
          <label className="flex flex-col text-xs">
            <span className="text-slate-600">P25</span>
            <input
              type="number"
              min={0}
              step="0.01"
              value={form.market_p25_salary}
              onChange={(e) => setField("market_p25_salary", e.target.value)}
              className="mt-1 rounded border border-slate-300 px-2 py-1 text-sm"
            />
          </label>
          <label className="flex flex-col text-xs">
            <span className="text-slate-600">P50</span>
            <input
              type="number"
              min={0}
              step="0.01"
              value={form.market_p50_salary}
              onChange={(e) => setField("market_p50_salary", e.target.value)}
              className="mt-1 rounded border border-slate-300 px-2 py-1 text-sm"
            />
          </label>
          <label className="flex flex-col text-xs">
            <span className="text-slate-600">P75</span>
            <input
              type="number"
              min={0}
              step="0.01"
              value={form.market_p75_salary}
              onChange={(e) => setField("market_p75_salary", e.target.value)}
              className="mt-1 rounded border border-slate-300 px-2 py-1 text-sm"
            />
          </label>
          <label className="flex flex-col text-xs">
            <span className="text-slate-600">P90</span>
            <input
              type="number"
              min={0}
              step="0.01"
              value={form.market_p90_salary}
              onChange={(e) => setField("market_p90_salary", e.target.value)}
              className="mt-1 rounded border border-slate-300 px-2 py-1 text-sm"
            />
          </label>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <label className="flex flex-col text-xs">
            <span className="text-slate-600">
              {t("comp_bands.market_source")}
            </span>
            <select
              value={form.market_data_source}
              onChange={(e) => setField("market_data_source", e.target.value)}
              className="mt-1 rounded border border-slate-300 px-2 py-1 text-sm"
            >
              <option value="">{t("comp_bands.cycle_none")}</option>
              <option value="mercer">Mercer</option>
              <option value="aon">Aon</option>
              <option value="wtw">WTW</option>
              <option value="internal">Internal</option>
              <option value="customer_uploaded">Customer Upload</option>
            </select>
          </label>
          <label className="flex flex-col text-xs">
            <span className="text-slate-600">
              {t("comp_bands.market_as_of")}
            </span>
            <input
              type="date"
              value={form.market_data_as_of}
              onChange={(e) => setField("market_data_as_of", e.target.value)}
              className="mt-1 rounded border border-slate-300 px-2 py-1 text-sm"
            />
          </label>
        </div>
      </fieldset>

      {error && (
        <div className="rounded border border-rose-300 bg-rose-50 px-3 py-2 text-xs text-rose-700">
          {error}
        </div>
      )}

      <button
        type="button"
        disabled={save.isPending}
        onClick={() => save.mutate()}
        className="inline-flex items-center gap-2 rounded bg-brand-deep px-3 py-2 text-sm text-white hover:bg-slate-900 disabled:opacity-50"
      >
        <Save className="h-4 w-4" />
        {save.isPending ? t("common.saving") : t("common.save")}
      </button>
    </div>
  );
}

export function CompBandsPage() {
  const { t } = useTranslation();
  const { legalEntityId, gate } = useLegalEntityGate();
  const queryClient = useQueryClient();
  const [selectedGradeId, setSelectedGradeId] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);

  const gradesQ = useQuery({
    queryKey: ["grades", legalEntityId],
    queryFn: () => api.listGrades(legalEntityId!),
    enabled: !!legalEntityId,
  });

  const bandsQ = useQuery({
    queryKey: ["comp-bands", legalEntityId, selectedGradeId],
    queryFn: () =>
      api.listCompBands(legalEntityId!, selectedGradeId ?? undefined),
    enabled: !!legalEntityId,
  });

  const grades = gradesQ.data ?? [];
  const bands = bandsQ.data ?? [];
  const bandsByGrade = useMemo(() => {
    const by: Record<string, CompBandDTO[]> = {};
    for (const b of bands) {
      (by[b.grade_id] ??= []).push(b);
    }
    return by;
  }, [bands]);

  const selectedGrade = useMemo<GradeDetail | null>(
    () => grades.find((g) => g.id === selectedGradeId) ?? null,
    [grades, selectedGradeId],
  );

  const selectedBands = selectedGradeId
    ? bandsByGrade[selectedGradeId] ?? []
    : [];

  if (gate) return gate;
  if (gradesQ.isPending) return <Loading />;
  if (gradesQ.isError)
    return <ErrorPanel message={(gradesQ.error as Error).message} />;

  return (
    <div className="space-y-4">
      <PageHeader
        title={t("comp_bands.title")}
        subtitle={t("comp_bands.subtitle")}
      />

      {grades.length === 0 ? (
        <EmptyState
          icon={Layers}
          headline={t("comp_bands.no_grades_title")}
          description={t("comp_bands.no_grades_desc")}
        />
      ) : (
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-12">
          <aside className="space-y-2 lg:col-span-4">
            <div className="text-xs font-medium uppercase tracking-wide text-slate-500">
              {t("comp_bands.grades")}
            </div>
            <ul className="space-y-1">
              {grades.map((g) => {
                const count = bandsByGrade[g.id]?.length ?? 0;
                const active = selectedGradeId === g.id;
                return (
                  <li key={g.id}>
                    <button
                      type="button"
                      onClick={() => {
                        setSelectedGradeId(g.id);
                        setCreating(false);
                      }}
                      className={`flex w-full items-center justify-between rounded border px-3 py-2 text-left text-sm ${
                        active
                          ? "border-brand-deep bg-slate-50"
                          : "border-slate-200 bg-white hover:border-slate-300"
                      }`}
                    >
                      <span>
                        <BilingualText en={g.name_en} ar={g.name_ar} />
                        <span className="ml-2 text-xs text-slate-500">
                          {g.code}
                        </span>
                      </span>
                      <span className="rounded bg-slate-100 px-2 py-0.5 text-xs text-slate-700">
                        {count}
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          </aside>

          <section className="space-y-3 lg:col-span-8">
            {selectedGrade ? (
              <>
                <div className="flex items-center justify-between">
                  <div className="text-sm font-medium text-slate-700">
                    {t("comp_bands.bands_for", {
                      code: selectedGrade.code,
                    })}
                  </div>
                  <button
                    type="button"
                    onClick={() => setCreating(true)}
                    className="inline-flex items-center gap-1 rounded border border-slate-300 bg-white px-2 py-1 text-xs hover:bg-slate-50"
                  >
                    <Plus className="h-3 w-3" />
                    {t("comp_bands.add_step")}
                  </button>
                </div>

                {selectedBands.length === 0 && !creating && (
                  <EmptyState
                    icon={Banknote}
                    headline={t("comp_bands.no_bands_title")}
                    description={t("comp_bands.no_bands_desc")}
                  />
                )}

                {creating && (
                  <BandForm
                    band={null}
                    legalEntityId={legalEntityId!}
                    gradeId={selectedGrade.id}
                    onSaved={() => {
                      setCreating(false);
                      queryClient.invalidateQueries({
                        queryKey: ["comp-bands", legalEntityId],
                      });
                    }}
                  />
                )}

                {selectedBands.map((b) => (
                  <BandForm
                    key={b.id}
                    band={b}
                    legalEntityId={legalEntityId!}
                    gradeId={selectedGrade.id}
                    onSaved={() =>
                      queryClient.invalidateQueries({
                        queryKey: ["comp-bands", legalEntityId],
                      })
                    }
                  />
                ))}
              </>
            ) : (
              <EmptyState
                icon={Banknote}
                headline={t("comp_bands.pick_grade_title")}
                description={t("comp_bands.pick_grade_desc")}
              />
            )}
          </section>
        </div>
      )}
    </div>
  );
}
